import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { getAllAcademicYear } from "../../api/generalParam";
import { getAllAcademicYearByStudent } from "../../api/student";
import { useAcademicYear } from "./context/AcademicYearContext";
import { useSelector } from "react-redux";
import { getTeacherYearCourse, getCurrentYear } from "../../api/edt";
import { getAllAcademicYearByParent } from "../../api/parents";
import { useChildrenDetails } from "./context/ChildrenDetailsContext";

// Définition du type attendu pour une année académique
type AcademicYear = {
  id: string;
  label: string;
  startDate: string;
  endDate: string;
  statut: boolean;
  Student?: {
    id: string;
    firstname: string;
    lastname: string;
    matricule: string;
    blocked_Note: boolean;
    Class?: {
      id: string;
      label: string;
      abbreviation: string;
    };
    Level?: {
      id: string;
      label: string;
      abbreviation: string;
    };
  };
};

interface Props {
  className?: string; // permet de passer des classes CSS depuis Header ou Sidebar
}

const AcademicYearSelect: React.FC<Props> = ({ className }) => {
  const { childrenDetails, selectedChild } = useChildrenDetails();
  const [selectedChildInfos, setSelectedChildInfos] = useState<any>();

  // Liste de toutes les années académiques récupérées depuis l'API
  const [allYears, setAllYears] = useState<AcademicYear[]>([]);

  // Contexte global (selectedYear = année sélectionnée, setSelectedYear = mise à jour)
  const { selectedYear, setSelectedYear } = useAcademicYear();

  // type de l'utilisateure, ces permissions et les datas du sidebar
  const userType = useSelector((state: any) => state.auth.userType);
  const userData = useSelector((state: any) => state.auth.user);

  const fetchData = async () => {
    try {
      let yearsResponse;

      switch (userType) {
        case "UserProfil":
          yearsResponse = await getAllAcademicYear();
          break;
        case "StudentProfil":
          yearsResponse = await getAllAcademicYearByStudent(userData.id);
          break;
        case "TeacherProfil":
          yearsResponse = await getTeacherYearCourse(userData.id);
          if (yearsResponse?.data?.length === 0) {
            // Si l'enseignant n'a pas de cours assignés, on récupère l'année en cours
            yearsResponse = await getCurrentYear();
          }
          break;
        case "ParentProfil":
          const response = await getAllAcademicYearByParent(userData.id);
          yearsResponse = selectedChild
            ? {
                ...response,
                data: response?.data?.filter((y: any) =>
                  y.student_ids.some((id: any) => id === selectedChild),
                ),
              }
            : response;
          // console.log("Years Response : ", yearsResponse);
          break;
      }

      const years = yearsResponse.data;
      // console.log("Années académiques récupérées:", years);
      setAllYears(years);

      return years;
    } catch (error: any) {
      console.error("Erreur récupération années académiques:", error);
      toast.error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Une erreur s'est produit, Réessayer plus tard",
      );
      setAllYears([]);
      return [];
    }
  };

  useEffect(() => {
    const init = async () => {
      // Vérifie si une année est déjà sauvegardée dans le localStorage
      const savedYear = localStorage.getItem("selectedAcademicYear");
      const years = await fetchData();

      if (savedYear) {
        try {
          const parsed = JSON.parse(savedYear);
          const found = years.find((y: AcademicYear) => y.id === parsed.id);
          if (found) {
            setSelectedYear(found);
            return;
          }
        } catch {}
      }

      const activeYear = years.find((y: AcademicYear) => y.statut === true);
      const fallback = activeYear || years[0];
      if (fallback) {
        setSelectedYear(fallback);
        localStorage.setItem("selectedAcademicYear", JSON.stringify(fallback));
      }
    };

    init();
    // Récupération des années académiques depuis l’API
    // Fonction générique pour récupérer les années académiques selon le type d'utilisateur
  }, [userType, selectedChild]);

  useEffect(() => {
    const matchedChild = childrenDetails.find(
      (c: any) => c.id === selectedChild,
    );
    if (matchedChild) setSelectedChildInfos(matchedChild);
  }, [selectedChild, childrenDetails]);

  // Lorsqu’on change d’année dans le <select>
  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const yearId = e.target.value;

    const freshYears = await fetchData(); // recharge BD
    const selected = freshYears.find((y: any) => y.id.toString() === yearId);

    if (selected) {
      setSelectedYear(selected); // met à jour le contexte global
      localStorage.setItem("selectedAcademicYear", JSON.stringify(selected)); // sauvegarde dans le navigateur
    }
  };

  return (
    <select
      value={selectedYear?.id?.toString() || ""}
      onChange={handleChange}
      className={`form-select form-select-sm ${className || ""}`}
    >
      {allYears.map((year) => (
        <option key={year.id} value={year.id.toString()}>
          {`Année ${year.label}`}
        </option>
      ))}
    </select>
  );
};

export default AcademicYearSelect;
